import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api',
  withCredentials: true,
  timeout: 20_000
});

// Token cache (in-memory). Refresh token lives in httpOnly cookie.
let accessToken = null;
let refreshPromise = null;

export function setAccessToken(token) {
  accessToken = token;
}

export function getAccessToken() {
  return accessToken;
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  // Attach unlock token for public invitation if present
  const unlockTokens = JSON.parse(sessionStorage.getItem('inv:unlock') || '{}');
  const slugMatch = config.url?.match(/\/public\/invitation\/([^/]+)/);
  if (slugMatch && unlockTokens[slugMatch[1]]) {
    config.headers = config.headers || {};
    config.headers['x-unlock-token'] = unlockTokens[slugMatch[1]];
  }
  return config;
});

api.interceptors.response.use(
  (r) => {
    // Reject if response is HTML (backend not reachable, got index.html instead)
    const contentType = r.headers?.['content-type'] || '';
    if (contentType.includes('text/html')) {
      return Promise.reject(new Error('Backend not reachable'));
    }
    return r;
  },
  async (error) => {
    const original = error.config;
    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/refresh') &&
      !original.url?.includes('/auth/login') &&
      !original.url?.includes('/auth/register')
    ) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api.post('/auth/refresh').then((res) => {
            setAccessToken(res.data.accessToken);
            return res.data.accessToken;
          }).finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        setAccessToken(null);
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
    }
    return Promise.reject(error);
  }
);

export default api;
